import type { StripeLineItem } from '@/actions/settings/billingActions'
import { CatchUpBkTreatment, catchUpBkTreatment } from '../payment/helpers'
import type {
  BuildCheckoutParamsInput,
  SignupCheckoutParams,
} from './signup-checkout.types'

/**
 * Not a suspension despite the namespace — it is the second checkout, which collects
 * catch up bookkeeping. Two sessions are needed because one Stripe session applies
 * only one coupon.
 */
const SECOND_CHECKOUT_PATH = '/accounts/suspended/catch-up-bookkeeping'

const CATCH_UP_BK_TRIAL_DISCLOSURE =
  '**A one-time fee of $99 per month of catch up bookkeeping will occur after your trial ends.  If you were given a discount on catch up bookkeeping, that will apply at the time you check out at the end of your trial.**'

export const buildCheckoutParams = ({
  userId,
  priceId,
  catchUpBookkeeping,
  freeTrialEnabled,
  freeTrialSearchParamValue,
  useTwoCheckoutFlow,
  isUpsell,
  successUrl,
  cancelUrl,
  cubkWaiverEnabled,
  serverWaived,
}: BuildCheckoutParamsInput): SignupCheckoutParams => {
  const treatment = catchUpBkTreatment({
    trialCode: freeTrialSearchParamValue,
    freeTrialEnabled,
    useTwoCheckoutFlow,
    waiverEnabled: cubkWaiverEnabled,
    serverWaived,
  })
  const lineItems: StripeLineItem[] = [{ priceId, quantity: 1 }]

  // Two-checkout users are charged, but in the second session — never a line item here.
  if (
    catchUpBookkeeping?.priceId &&
    treatment === CatchUpBkTreatment.chargeAtSignup &&
    !useTwoCheckoutFlow
  ) {
    lineItems.push({
      priceId: catchUpBookkeeping.priceId,
      quantity: catchUpBookkeeping.monthCount,
    })
  }

  return {
    userId,
    lineItems,
    success_url: {
      url: useTwoCheckoutFlow ? SECOND_CHECKOUT_PATH : successUrl,
    },
    cancel_url: { url: cancelUrl },
    // Only these two fields are nulled — the catch up predicates read the raw value.
    // Normalizing it once would charge an upsell user on a trial.
    freeTrialEnabled: isUpsell ? undefined : freeTrialEnabled,
    freeTrialSearchParamValue: isUpsell ? null : freeTrialSearchParamValue,
    custom_text:
      treatment === CatchUpBkTreatment.deferToPostTrial
        ? { submit: { message: CATCH_UP_BK_TRIAL_DISCLOSURE } }
        : undefined,
  }
}
